import { useGetAllDepartmentsQuery, useGetAllEmployeesQuery, useGetAllDesignationsQuery } from "@/app/features/employee/emp-dashboard/emp-dashboard.api";
import { useGetAllAdminKPIPlanQuery } from "@/app/features/employee/performance/performance.api";
import { ChevronDownIcon } from "@/icons";
import { Button, Flex, Select } from "antd";
import {
  DISCLOSER_KEYS,
  FILTER_KEYS,
} from "../../../../../constants/keys.constants";
import {
  useAppDiscloser,
  useAppFilters,
  useSelectionOptions,
} from "../../../../../hooks";
import useAuth from "../../../../../hooks/useAuth";
import type { PerformanceSegmentType } from "../EmpPerformance";

type Props = { type: PerformanceSegmentType };

const SummaryFilters = () => {
  const { organizationId } = useAuth();
  const { handleFilter, filters } = useAppFilters(
    FILTER_KEYS.PERFORMANCE_SUMMARY,
  );

  const [planOptions, isPlanLoading] = useSelectionOptions(
    useGetAllAdminKPIPlanQuery,
    {
      labelKey: "planName",
      valueKey: "id",
      dataKey: "plans",
      params: { organization_id: organizationId },
    },
  );

  const [deptOptions, isDeptLoading] = useSelectionOptions(
    useGetAllDepartmentsQuery,
    {
      labelKey: "name",
      valueKey: "id",
      params: { organizationId },
    },
  );

  const [managerOptions, isManagerLoading] = useSelectionOptions(
    useGetAllEmployeesQuery,
    {
      labelKey: "name",
      valueKey: "user_id",
      params: { params: { limit: 1000, status: "active" } },
    },
  );

  return (
    <div className="flex flex-col items-end gap-4">
      <div className="flex flex-col items-end gap-4">
        <Flex gap={16} align="center" className="flex-wrap lg:flex-nowrap">
          <div className="min-w-[200px]">
            <Select
              allowClear
              size="large"
              placeholder="Select KPI Set-Up"
              showSearch
              optionFilterProp="label"
              popupMatchSelectWidth={false}
              placement="topRight"
              loading={isPlanLoading}
              options={planOptions}
              value={filters.plan_id}
              onChange={(val) => handleFilter("plan_id", val)}
              suffixIcon={<ChevronDownIcon className="text-white" size={24} />}
            />
          </div>
          <div className="min-w-[180px]">
            <Select
              allowClear
              size="large"
              placeholder="Department"
              showSearch
              optionFilterProp="label"
              popupMatchSelectWidth={false}
              placement="topRight"
              loading={isDeptLoading}
              options={deptOptions}
              value={filters.department_id}
              onChange={(val) => handleFilter("department_id", val)}
              suffixIcon={<ChevronDownIcon className="text-white" size={24} />}
            />
          </div>
          <div className="min-w-[180px]">
            <Select
              allowClear
              size="large"
              placeholder="Line Manager"
              showSearch
              optionFilterProp="label"
              popupMatchSelectWidth={false}
              placement="topRight"
              loading={isManagerLoading}
              options={managerOptions}
              value={filters.manager_id}
              onChange={(val) => handleFilter("manager_id", val)}
              suffixIcon={<ChevronDownIcon className="text-white" size={24} />}
            />
          </div>
        </Flex>
      </div>
      <div className="h-[44px]" />
    </div>
  );
};

const OpenKpiSetUpFilters = () => {
  const { filters, handleFilter } = useAppFilters(
    FILTER_KEYS.PERFORMANCE_OPEN_KPI,
  );
  const [_config, handleOpen] = useAppDiscloser(
    DISCLOSER_KEYS.PERFORMANCE_OPEN_KPI,
  );

  const [departmentOptions] = useSelectionOptions(useGetAllDepartmentsQuery, {
    labelKey: "name",
    valueKey: "id",
  });

  return (
    <div className="flex flex-col items-end gap-4">
      <div className="flex flex-col items-end gap-4">
        <Flex gap={16} align="center" className="flex-wrap lg:flex-nowrap">
          <Select
            allowClear
            size="large"
            placeholder="Department"
            showSearch
            optionFilterProp="label"
            options={departmentOptions}
            popupMatchSelectWidth={false}
            placement="topRight"
            value={filters.department_id}
            onChange={(val) => handleFilter("department_id", val)}
            suffixIcon={<ChevronDownIcon className="text-white" size={24} />}
          />
          <Select
            allowClear
            size="large"
            placeholder="Plan Period"
            value={filters.plan_period}
            onChange={(val) => handleFilter("plan_period", val)}
            options={[
              { label: "Yearly", value: "YEARLY" },
              { label: "Half Yearly", value: "HALF_YEARLY" },
              { label: "Quarterly", value: "QUARTERLY" },
            ]}
            suffixIcon={<ChevronDownIcon className="text-white" size={24} />}
          />
        </Flex>
        <Button
          size="large"
          className="btn-primary !text-theme-gray-650 !font-bold hover:!text-theme-gray-650 !rounded-md"
          onClick={() => handleOpen({ type: "ADD" })}
        >
          Open KPI Set-Up
        </Button>
      </div>
    </div>
  );
};

const KpiSetUpReportFilters = () => {
  const { organizationId } = useAuth();
  const { handleFilter, filters } = useAppFilters(
    FILTER_KEYS.PERFORMANCE_KPI_REPORT,
  );

  const [planOptions, isPlanLoading] = useSelectionOptions(
    useGetAllAdminKPIPlanQuery,
    {
      labelKey: "planName",
      valueKey: "id",
      dataKey: "plans",
      params: { organization_id: organizationId },
    },
  );

  const [deptOptions, isDeptLoading] = useSelectionOptions(
    useGetAllDepartmentsQuery,
    {
      labelKey: "name",
      valueKey: "id",
      params: { organizationId },
    },
  );

  const STATUS_OPTIONS = [
    { label: "Not Started", value: "NOT_STARTED" },
    { label: "Pending", value: "PENDING" },
    { label: "Draft", value: "DRAFT" },
    { label: "Submitted", value: "SUBMITTED" },
    { label: "Approved", value: "APPROVED" },
  ];

  return (
    <div className="flex flex-col items-end gap-4">
      <div className="flex flex-col items-end gap-4">
        <Flex gap={16} align="center" className="flex-wrap lg:flex-nowrap">
          <div className="min-w-[200px]">
            <Select
              allowClear
              size="large"
              placeholder="Select KPI Set-Up"
              showSearch
              optionFilterProp="label"
              popupMatchSelectWidth={false}
              placement="topRight"
              loading={isPlanLoading}
              options={planOptions}
              value={filters.plan_id}
              onChange={(val) => handleFilter("plan_id", val)}
              suffixIcon={<ChevronDownIcon className="text-white" size={24} />}
            />
          </div>
          <div className="min-w-[150px]">
            <Select
              allowClear
              size="large"
              placeholder="Department"
              showSearch
              optionFilterProp="label"
              popupMatchSelectWidth={false}
              placement="topRight"
              loading={isDeptLoading}
              options={deptOptions}
              value={filters.department_id}
              onChange={(val) => handleFilter("department_id", val)}
              suffixIcon={<ChevronDownIcon className="text-white" size={24} />}
            />
          </div>
          <div className="min-w-[100px]">
            <Select
              allowClear
              size="large"
              placeholder="Status"
              popupMatchSelectWidth={false}
              placement="topRight"
              options={STATUS_OPTIONS}
              value={filters.kpi_status}
              onChange={(val) => handleFilter("kpi_status", val)}
              suffixIcon={<ChevronDownIcon className="text-white" size={24} />}
            />
          </div>
        </Flex>
      </div>
      <div className="h-[44px]" />
    </div>
  );
};

const AppraisalReportFilters = () => {
  const { organizationId } = useAuth();
  const { handleFilter, filters } = useAppFilters(
    FILTER_KEYS.PERFORMANCE_APPRAISAL_REPORT,
  );
  const [_config, handleOpen] = useAppDiscloser(
    DISCLOSER_KEYS.PERFORMANCE_APPRISAL_REPORT,
  );

  const [planOptions, isPlanLoading] = useSelectionOptions(
    useGetAllAdminKPIPlanQuery,
    {
      labelKey: "planName",
      valueKey: "id",
      dataKey: "plans",
      params: { organization_id: organizationId },
    },
  );

  const [deptOptions, isDeptLoading] = useSelectionOptions(
    useGetAllDepartmentsQuery,
    {
      labelKey: "name",
      valueKey: "id",
      params: { organizationId },
    },
  );

  const [designationOptions, isDesgLoading] = useSelectionOptions(
    useGetAllDesignationsQuery,
    {
      labelKey: "name",
      valueKey: "id",
      params: { organizationId },
    },
  );

  const STATUS_OPTIONS = [
    { label: "Not Started", value: "NOT_STARTED" },
    { label: "KPI Set-Up Start", value: "PENDING" },
    { label: "Approved", value: "APPROVED" },
    { label: "Submitted", value: "SUBMITTED" },
    { label: "Rejected", value: "REJECTED" },
  ];

  return (
    <div className="flex flex-col items-end gap-4">
      <Flex gap={16} align="center" className="flex-wrap lg:flex-nowrap">
        <div className="min-w-[200px]">
          <Select
            allowClear
            size="large"
            placeholder="Select Plan"
            showSearch
            optionFilterProp="label"
            popupMatchSelectWidth={false}
            placement="topRight"
            loading={isPlanLoading}
            options={planOptions}
            value={filters.plan_id}
            onChange={(val) => handleFilter("plan_id", val)}
            suffixIcon={<ChevronDownIcon className="text-white" size={24} />}
          />
        </div>
        <div className="min-w-[150px]">
          <Select
            allowClear
            size="large"
            placeholder="Department"
            showSearch
            optionFilterProp="label"
            popupMatchSelectWidth={false}
            placement="topRight"
            loading={isDeptLoading}
            options={deptOptions}
            value={filters.department_id}
            onChange={(val) => handleFilter("department_id", val)}
            suffixIcon={<ChevronDownIcon className="text-white" size={24} />}
          />
        </div>
        <div className="min-w-[150px]">
          <Select
            allowClear
            size="large"
            placeholder="Designation"
            showSearch
            optionFilterProp="label"
            popupMatchSelectWidth={false}
            placement="topRight"
            loading={isDesgLoading}
            options={designationOptions}
            value={filters.designation_id}
            onChange={(val) => handleFilter("designation_id", val)}
            suffixIcon={<ChevronDownIcon className="text-white" size={24} />}
          />
        </div>
        <div className="min-w-[120px]">
          <Select
            allowClear
            size="large"
            placeholder="Status"
            popupMatchSelectWidth={false}
            placement="topRight"
            options={STATUS_OPTIONS}
            value={filters.appraisal_status}
            onChange={(val) => handleFilter("appraisal_status", val)}
            suffixIcon={<ChevronDownIcon className="text-white" size={24} />}
          />
        </div>
      </Flex>
      <Button
        size="large"
        className="btn-primary !text-theme-gray-650 !font-bold hover:!text-theme-gray-650 !rounded-md"
        onClick={() => handleOpen({ type: "ADD" })}
      >
        Export
      </Button>
    </div>
  );
};

const Filters = ({ type }: Props) => {
  switch (type) {
    case "emp-performance-summery":
      return <SummaryFilters />;
    case "emp-performance-open-kpi":
      return <OpenKpiSetUpFilters />;
    case "emp-performance-kpi-report":
      return <KpiSetUpReportFilters />;
    case "emp-performance-appraisal-report":
      return <AppraisalReportFilters />;
    default:
      return null;
  }
};
export default Filters;
